import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { FilterValues } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { makePostCallForOncologySubmission } from "../apis/makePostCallForOncologySubmission";
import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import {
  OncologySubmissionFilterPayload,
  ONCOLOGY_SUBMISSION_DEFAULT_ORDER_BY,
  useFilterPayload,
} from "./useFilterPayload";
import { OncologySubmissionTableState } from "./useOncologySubmissionTableData";
import { usePaginationReset } from "./usePaginationReset";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

export interface UseFilterAndSearchReturn {
  filterPayload: OncologySubmissionFilterPayload | null;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  paginationResetCount: number;
  validFilters: FilterValues[];
  /**
   * Re-runs the oncology submission list POST. With no args, repeats the current search/filters.
   * With `rowSeq`, sends `filters.seq` only (no `dumped_at`), same shape as searching by MRN in the table.
   *
   * @param rowSeq - Optional order sequence/MRN to filter by (e.g. Biosimilar View)
   */
  refetchWithCurrentSearchAndFilters: (rowSeq?: string) => void;
}

/**
 * Manages oncology submission table search term, filter-derived POST payload, and the effect that
 * loads rows when those inputs change. Exposes `refetchWithCurrentSearchAndFilters` to repeat the
 * same POST without altering search or filters, or a seq-only payload when a row sequence is passed
 * (Biosimilar "View").
 *
 * @param initialDataFetchingExecuted - When true, filter payload is built and fetch effects run
 * @param setTableData - Setter for row data from the API
 * @param setOncologySubmissionState - Setter for loading/error/total_count state
 * @param dateFromStart - Default range start (YYYYMMDD)
 * @param dateFromEnd - Default range end (YYYYMMDD)
 * @param pageSize - Page size for the list request
 * @returns Search state, filters, pagination reset count, and manual refetch callback
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  setOncologySubmissionState: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableState>
  >,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
): UseFilterAndSearchReturn => {
  const { searchTerm, setSearchTerm } = useSearchTerm();

  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();

  const validFilters = useValidFilters();

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setOncologySubmissionState,
    setPaginationResetCount,
  );

  const refetchWithCurrentSearchAndFilters = useCallback(
    (rowSeq?: string) => {
      if (!initialDataFetchingExecuted) {
        return;
      }
      const trimmedSeq = rowSeq?.trim() ?? "";
      if (trimmedSeq.length > 0) {
        const seqOnlyPayload: OncologySubmissionFilterPayload = {
          filters: { seq: [trimmedSeq] },
          page: 1,
          page_size: pageSize,
          request_id: uuidv4(),
          order_by: ONCOLOGY_SUBMISSION_DEFAULT_ORDER_BY,
        };
        void makePostCallForOncologySubmission(
          seqOnlyPayload,
          setOncologySubmissionState,
          setTableData,
          setPaginationResetCount,
        );
        return;
      }
      if (!filterPayload) {
        return;
      }
      void makePostCallForOncologySubmission(
        { ...filterPayload, request_id: uuidv4() },
        setOncologySubmissionState,
        setTableData,
        setPaginationResetCount,
      );
    },
    [
      filterPayload,
      initialDataFetchingExecuted,
      pageSize,
      setOncologySubmissionState,
      setTableData,
      setPaginationResetCount,
    ],
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    validFilters,
    refetchWithCurrentSearchAndFilters,
  };
};
